import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
    "cursor-pointer focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-full border border-transparent bg-clip-padding text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none in-data-[slot=button-group]:rounded-none in-data-[slot=button-group]:first:rounded-l-md in-data-[slot=button-group]:last:rounded-r-md",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/80",
                outline:
                    "border-border bg-background hover:bg-muted hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground shadow-xs",
                secondary:
                    "bg-secondary text-secondary-foreground hover:bg-zinc-200 aria-expanded:bg-zinc-200 aria-expanded:text-secondary-foreground",
                ghost: "hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground",
                destructive:
                    "bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
                link: "text-primary underline-offset-4 hover:underline",
            },
            size: {
                default:
                    "h-9 gap-1.5 px-3.5 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 has-[>svg:first-child:not(:only-child)]:pl-2.5 has-[>svg:last-child:not(:only-child)]:pr-2.5",
                xs: "h-6 gap-1 rounded-full px-3 text-xs has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 has-[>svg:first-child:not(:only-child)]:pl-2 has-[>svg:last-child:not(:only-child)]:pr-2 [&_svg:not([class*='size-'])]:size-3",
                sm: "h-8 gap-1 rounded-full px-3.5 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 has-[>svg:first-child:not(:only-child)]:pl-2.5 has-[>svg:last-child:not(:only-child)]:pr-2.5",
                lg: "h-10 gap-1.5 px-3.5 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 has-[>svg:first-child:not(:only-child)]:pl-2.5 has-[>svg:last-child:not(:only-child)]:pr-2.5",
                icon: "size-9",
                "icon-xs": "size-6 rounded-full [&_svg:not([class*='size-'])]:size-3",
                "icon-sm": "size-8 rounded-full",
                "icon-lg": "size-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

function Button({
    className,
    variant = "default",
    size = "default",
    disabled,
    disabledReason,
    ...props
}: ButtonPrimitive.Props &
    VariantProps<typeof buttonVariants> & {
        /** Hover hint while disabled, explaining how to enable the button. */
        disabledReason?: string;
    }) {
    const button = (
        <ButtonPrimitive
            data-slot="button"
            disabled={disabled}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );

    if (!disabled) return button;

    const trigger = (
        <span className="inline-flex cursor-not-allowed self-stretch has-[>.w-full]:w-full [&>*]:w-full">
            {button}
        </span>
    );

    if (!disabledReason) return trigger;

    return (
        <TooltipProvider delay={200}>
            <Tooltip>
                <TooltipTrigger render={trigger} />
                <TooltipContent positionerClassName="z-[600]">
                    {disabledReason}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export { Button, buttonVariants };
